import moment from 'moment';
import {
  BotComplexParams,
  BotTimeFrames,
  Candle,
  ComplexParamsBundle,
} from '../screens/Detail/types';
import { Transaction } from '../screens/Overview/types';
import { Transaction as ProfitTransaction } from '../screens/ProfitCalculations/types';
import { formatPercentage, formatPrice } from './NumberFormatter';
import { getTimeFrameIntervalTimestamp } from '../screens/Detail/Charts/PriceChart/helpers';
import { CustomTransaction } from 'grts-utils/types/Transaction';
import {
  CompactTransaction,
  DetailCompactTransaction,
  DetailTransaction,
} from '../types/subscription';
import { Bot } from 'grts-utils/types/Bot';
import { GenericTransactionInfo } from '../types/Transaction';
import { Exchange } from 'grts-utils/types/Exchange';
import { TimeFrame } from '../types/Bot';

export const formatCorrectDecimals = (value: number, decimals = 2) => {
  const valueString = Intl.NumberFormat('en-US', {
    maximumFractionDigits: 10,
    useGrouping: false,
  }).format(value);
  const pointIndex = valueString.indexOf('.');

  if (pointIndex === -1) {
    // TODO, add error, wrong value / no decimals
    return value;
  }

  const amountOfDecimals = valueString.length - pointIndex - 1;

  if (amountOfDecimals < decimals) {
    return value;
  }

  const formattedValue = valueString.substring(0, pointIndex + decimals + 1);
  return parseFloat(formattedValue);
};

export const findTransactionWave = (
  candles: Candle[],
  startTimestamp: number,
  endTimestamp: number,
) => {
  /** If we have a buy,
   * we need the wave from buy to next sell;
   * and a wave red (or small area before the start of green wave)
   * and wave green
   * and (little red at the end)
   *
   *
   * If we have a sell,
   * we need the wave from previous buy to sell;
   * and a wave green (or small period)
   * and wave red
   * and little green at the end
   */
  if (candles.length === 0) {
    return candles;
  }

  const startTransactionCandleIndex = candles.findIndex(
    (c) =>
      c.startTimestamp <= startTimestamp && startTimestamp <= c.endTimestamp,
  );

  const endTransactionCandleIndex = candles.findIndex(
    (c) => c.startTimestamp <= endTimestamp && endTimestamp <= c.endTimestamp,
  );

  if (startTransactionCandleIndex === -1 || endTransactionCandleIndex === -1) {
    return [];
  }

  return candles.slice(
    Math.max(0, startTransactionCandleIndex - 1),
    Math.min(endTransactionCandleIndex + 2, candles.length),
  );
};

/* ==============================================   createHeikinCandle   ============================================== */
export const createHeikinCandle = (
  currentNormalCandle: Candle | null,
  previousHeikinCandle?: Candle | null,
) => {
  if (!currentNormalCandle) {
    return null;
  }

  const open =
    ((previousHeikinCandle?.open ?? currentNormalCandle.open) +
      (previousHeikinCandle?.close ?? currentNormalCandle.close)) /
    2;
  const close =
    (currentNormalCandle.open +
      currentNormalCandle.high +
      currentNormalCandle.low +
      currentNormalCandle.close) /
    4;
  const low = currentNormalCandle.low;
  const high = currentNormalCandle.high;

  const direction = open > close ? 'down' : 'up';

  const heikinCandle = {
    ...currentNormalCandle,
    low:
      direction === 'up'
        ? open < low
          ? open
          : low
        : close < low
        ? close
        : low,
    open,
    close,
    high:
      direction === 'up'
        ? close > high
          ? close
          : high
        : open > high
        ? open
        : high,
    direction,
    startOfNewTrend: previousHeikinCandle
      ? previousHeikinCandle.direction !== direction
      : null,
    didMakeDecision: null,
    decision: null,
  };

  return heikinCandle;
};

/* ==============================================   transformToHeikinCandles   ============================================== */
/** Difference here is that you can pass a list of normal candles instead of 1 */
export const transformToHeikinCandles = (normalCandles: Candle[]): Candle[] => {
  const heikinCandles: Candle[] = [];

  normalCandles.forEach((candle) => {
    const heikinCandle = createHeikinCandle(
      candle,
      heikinCandles[heikinCandles.length - 1],
    );
    heikinCandles.push(heikinCandle!);
  });

  return heikinCandles;
};

export const formatTransactionType = (
  transactions: ProfitTransaction[],
  bot: Bot,
) => {
  return transactions.map((t, i) => {
    return {
      index: t.index || i + 1,
      id: Math.floor(Math.random() * 9999999999),
      botId: 1,
      baseAsset: bot.baseAsset,
      quoteAsset: bot.quoteAsset,
      transactionIndex: t.id,
      compactTransactionId: t.compactTransactionId
        ? t.compactTransactionId.toString()
        : null,
      orderId: null,
      orderType: t.orderType,
      transactionType: t.transactionType,
      orderPrice: t.orderPrice,
      assetQuantity: t.assetQuantity,
      stableQuantity: t.stableQuantity,
      currentAssetBalance: t.balanceInAsset,
      currentStableBalance: t.balanceInUsdt,
      totalBalance: t.balanceInUsdt + t.balanceInAsset * t.orderPrice,
      currentFee: t.currentFee,
      totalFee: t.totalFee,
      isComplete: t.isComplete ? 1 : 0,
      success: t.success,
      profit: t.orderType === 'BUY' ? t.profitInAsset : t.profitInUsdt,
      currentProfit: null,
      transactionTimestamp: t.timestamp,
      transactionDate: t.date,
      initialVolume: 0,
      initialVolumeAsset: 'USD',
      currentCbr: null,
      cbrt: 0,
      shorterTimeFrameInterval: 1,
      mrcEjectedPrice: null,
      mrcAcceptedPrice: null,
      multiOrderVolume: null,
      multiOrderIndex: null,
      customBalanceUsed: null,
      customBalanceSizeUsed: null,
      extraInfo: null,
      actions: null,
      averageOrderPrice: (t as any).averageOrderPrice,
      candles: t.candles,
    } as Transaction;
  });
};

export const isCustomTransaction = (
  transaction:
    | Transaction
    | CompactTransaction
    | CustomTransaction
    | GenericTransactionInfo,
): transaction is CustomTransaction =>
  transaction.transactionType.indexOf('BALANCE') !== -1;

export const generateTooltipLabel = (
  transaction: DetailCompactTransaction | CustomTransaction,
  bot?: Bot,
  isProfitCalcs?: boolean,
) => {
  const formattedDate = moment(transaction.transactionTimestamp).format(
    'DD MMM YYYY',
  );

  if (isCustomTransaction(transaction)) {
    const isStableTransaction =
      transaction.transactionType.indexOf('STABLE') !== -1;
    const quantity = isStableTransaction
      ? `Quantity: ${transaction.stableQuantity} ${bot?.quoteAsset}`
      : `Quantity: ${transaction.assetQuantity} ${bot?.baseAsset}`;

    return `Date: ${formattedDate}\n${quantity}`;
  }

  const profit = transaction.profit || 0;
  const orderType = transaction.orderType;

  return `${transaction.index}. ${transaction.orderType}${
    typeof isProfitCalcs === 'undefined' ? '' : isProfitCalcs ? ' (PC)' : ' (R)'
  }\nDate: ${formattedDate}\nPrice: ${formatPrice(
    transaction.averageOrderPrice || transaction.orderPrice,
  )}\n${orderType === 'BUY' ? 'A.' : 'S.'} Profit: ${formatPercentage(profit)}`;
};

export const generateTooltipLabelGenericTransaction = (
  transaction: GenericTransactionInfo | CustomTransaction,
) => {
  const formattedDate = moment(transaction.transactionTimestamp).format(
    'DD MMM YYYY',
  );

  if (isCustomTransaction(transaction)) {
    const isStableTransaction =
      transaction.transactionType.indexOf('STABLE') !== -1;
    const quantity = isStableTransaction
      ? `Quantity: ${transaction.stableQuantity}`
      : `Quantity: ${transaction.assetQuantity}`;

    return `Date: ${formattedDate}\n${quantity}`;
  }

  if (typeof transaction.orderType === undefined) {
    const isStableTransaction =
      transaction.transactionType.indexOf('STABLE') !== -1;
    const quantity = isStableTransaction
      ? `Quantity: ${transaction.stableQuantity} ${transaction.quoteAsset}`
      : `Quantity: ${transaction.assetQuantity} ${transaction.baseAsset}`;

    return `Date: ${formattedDate}\n${quantity}`;
  }

  const profit = transaction.profitInUsdt || transaction.profitInAsset || 0;
  const orderType = transaction.orderType;

  const transactionType = `${
    transaction.transactionType.includes('STOP_LOSS')
      ? 'STOP LOSS'
      : transaction.transactionType.includes('TAKE_PROFIT')
      ? 'TAKE PROFIT'
      : 'CORE TRANSACTION'
  } ${transaction.orderType}`;

  return `${
    transaction.transactionIndex
  }. ${transactionType}\nDate: ${formattedDate}\nPrice: ${formatPrice(
    transaction.orderPrice,
  )}\n${orderType === 'BUY' ? 'A.' : 'S.'} Profit: ${formatPercentage(profit)}`;
};

export const fillTransactionIndexes = (
  transactions: Array<Transaction | DetailTransaction>,
  compactTransactions: Array<Transaction | DetailCompactTransaction>,
) => {
  if (!transactions) {
    return transactions;
  }

  const allCompactTransactions = [...compactTransactions].sort(
    (a, b) => a.transactionTimestamp - b.transactionTimestamp,
  );
  const allTransactions = [...transactions].sort(
    (a, b) => a.transactionTimestamp - b.transactionTimestamp,
  );

  let compactTransactionIndex = 0;

  for (let i = 0; i < allCompactTransactions.length; i++) {
    const compactTransaction = allCompactTransactions[i];

    if (isCustomTransaction(compactTransaction)) {
      continue;
    }

    compactTransactionIndex++;
    compactTransaction.index = compactTransactionIndex.toString();

    /** Do the same but for individual transactions */
    const individualTransactions = allTransactions.filter(
      (t) => t.compactTransactionId === compactTransaction.compactTransactionId,
    );

    for (let j = 0; j < individualTransactions.length; j++) {
      const individualTransaction = individualTransactions[j];
      individualTransaction.index = `${compactTransactionIndex}.${j + 1}`;
    }
  }

  return [allTransactions, allCompactTransactions];
};

export const compareComplexParamsBundle = (
  complexParamsBundle: ComplexParamsBundle,
  transaction: GenericTransactionInfo,
) => {
  return (
    !!transaction.complexParams &&
    !!transaction.timeFrames &&
    (transaction.complexParams as BotComplexParams).cbrt ===
      complexParamsBundle.complexParams.cbrt &&
    (transaction.complexParams as BotComplexParams).shorterTimeFrameInterval ===
      complexParamsBundle.complexParams.shorterTimeFrameInterval &&
    transaction.timeFrames.higher === complexParamsBundle.timeFrames.higher &&
    transaction.timeFrames.shorter === complexParamsBundle.timeFrames.shorter &&
    transaction.timeFrames.multiOrder ===
      complexParamsBundle.timeFrames.multiOrder
  );
};

export const getFormatUnit = (
  startTimestamp: number,
  endTimestamp: number,
  includeHours = true,
) => {
  const range = endTimestamp - startTimestamp;

  return range < getTimeFrameIntervalTimestamp(TimeFrame.FiveDays) &&
    includeHours
    ? 'hh:mm'
    : range < getTimeFrameIntervalTimestamp(TimeFrame.TwoMonths) * 2
    ? 'DD MMM'
    : 'MMM YYYY';
};

export const createXTickValues = (
  startTimstamp: number,
  endTimstamp?: number,
  amount = 12,
) => {
  const end = endTimstamp || new Date().valueOf();

  if (startTimstamp >= end) {
    return [startTimstamp];
  }

  const range = end - startTimstamp;
  const formatUnit = getFormatUnit(startTimstamp, end);
  const ticks: number[] = [];

  if (formatUnit === 'MMM YYYY') {
    // Pick a monthly step: 1, 2, 3, 6 or 12 months — always equal intervals at start-of-month
    const totalMonths = moment(end).diff(moment(startTimstamp), 'months') + 1;
    const stepChoices = [1, 2, 3, 6, 12];
    const monthsPerTick =
      stepChoices.find((s) => totalMonths / s <= amount) ?? 12;

    let cursor = moment(startTimstamp).utc().startOf('month');
    if (cursor.valueOf() < startTimstamp) cursor = cursor.add(1, 'month');
    while (cursor.valueOf() <= end) {
      ticks.push(cursor.valueOf());
      cursor = cursor.add(monthsPerTick, 'months');
    }
  } else if (formatUnit === 'DD MMM') {
    // Pick a daily step: 1, 2, 3, 5, 7, 14 days — always equal intervals at start-of-day
    const totalDays = Math.ceil(range / (24 * 60 * 60 * 1000)) + 1;
    const stepChoices = [1, 2, 3, 5, 7, 14, 30];
    const daysPerTick = stepChoices.find((s) => totalDays / s <= amount) ?? 30;
    const intervalMs = daysPerTick * 24 * 60 * 60 * 1000;

    let cursor = moment(startTimstamp).utc().startOf('day');
    if (cursor.valueOf() < startTimstamp) cursor = cursor.add(1, 'day');
    let cur = cursor.valueOf();
    while (cur <= end) {
      ticks.push(cur);
      cur += intervalMs;
    }
  } else {
    // 'hh:mm' — Pick an hourly step: 1, 2, 3, 4, 6, 8, 12, 24 hours
    const totalHours = Math.ceil(range / (60 * 60 * 1000)) + 1;
    const stepChoices = [1, 2, 3, 4, 6, 8, 12, 24];
    const hoursPerTick =
      stepChoices.find((s) => totalHours / s <= amount) ?? 24;
    const intervalMs = hoursPerTick * 60 * 60 * 1000;

    let cursor = moment(startTimstamp).utc().startOf('hour');
    if (cursor.valueOf() < startTimstamp) cursor = cursor.add(1, 'hour');
    let cur = cursor.valueOf();
    while (cur <= end) {
      ticks.push(cur);
      cur += intervalMs;
    }
  }

  return ticks;
};

export const findClosestCandleTimestamp = (
  timestamp: number,
  candleDataPoints: Candle[],
) => {
  if (!candleDataPoints) {
    return timestamp;
  }

  return (
    candleDataPoints.find(
      (c) => c.startTimestamp <= timestamp && timestamp <= c.endTimestamp,
    )?.startTimestamp || 0
  );
};

// Only return what we are using
// + what needs to be added (like candles)
export const formatProfitTransactionsToGenericTransactions = (
  transactions: ProfitTransaction[],
  baseAsset: string,
  quoteAsset: string,
  complexParams: Partial<BotComplexParams> | undefined,
  timeFrames: BotTimeFrames | undefined,
): GenericTransactionInfo[] => {
  return transactions.map((t) => {
    return {
      transactionIndex: t.id,
      currentAssetBalance: t.balanceInAsset,
      currentStableBalance: t.balanceInUsdt,
      assetQuantity: t.assetQuantity,
      stableQuantity: t.stableQuantity,
      currentFee: t.currentFee,
      totalFee: t.totalFee,
      transactionTimestamp: t.timestamp,
      orderPrice: t.orderPrice,
      orderType: t.orderType,
      profitInAsset: t.profitInAsset,
      profitInUsdt: t.profitInUsdt,
      totalBalanceValue: t.totalBalanceValue,
      transactionType: t.transactionType,
      compactTransactionId: t.compactTransactionId,
      success: t.success,
      exchange: t.exchange,
      baseAsset,
      quoteAsset,
      complexParams,
      timeFrames,
      candles: t.candles,
      signals: t.signals,
      rsiThreshold: t.rsiThreshold,
      rsiValues: t.rsiValues,
      takeProfitSize: t.takeProfitSize,
      stopLossThreshold: t.priceThreshold,
      originalId: t.originalId,
    };
  });
};

// Only return what we are using
// + what needs to be added (like candles)
export const formatRealTransactionsToGenericTransactions = (
  transactions: Array<DetailCompactTransaction | CustomTransaction>,
  baseAsset: string,
  quoteAsset: string,
): Array<GenericTransactionInfo | CustomTransaction> => {
  return transactions.map((t) => {
    const getIsCustomTransaction = (
      transaction: DetailCompactTransaction | CustomTransaction,
    ): transaction is CustomTransaction =>
      transaction.transactionType.indexOf('BALANCE') !== -1;

    const isCustomTransaction = getIsCustomTransaction(t);

    if (isCustomTransaction) {
      return t;
    }

    return {
      transactionIndex: t.transactionIndex,
      currentAssetBalance: t.currentAssetBalance,
      currentStableBalance: t.currentStableBalance,
      assetQuantity: t.assetQuantity,
      stableQuantity: t.stableQuantity,
      currentFee: t.currentFee,
      totalFee: t.totalFee,
      transactionTimestamp: t.transactionTimestamp,
      orderPrice: t.orderPrice,
      orderType: t.orderType,
      profitInAsset: t.orderType === 'BUY' ? t.profit ?? 0 : 0,
      profitInUsdt: t.orderType === 'SELL' ? t.profit ?? 0 : 0,
      totalBalanceValue: t.totalBalance,
      transactionType: t.transactionType,
      compactTransactionId: t.compactTransactionId,
      success: t.success,
      exchange: t.exchange as Exchange,
      baseAsset,
      quoteAsset,
      complexParams: t.complexParams,
      timeFrames: t.timeFrames,
      //        candles?: {
      //    higher: Candle[];
      //    shorter: Candle[];
      //    multi: Candle[];
      //  };
      //     signals?: IndicatorDataPoint[];
    };
  });
};
